import log from 'loglevel';
import { geoPermStatus } from "./Constants";
import AppService from "./AppService";
import { Geolocation } from '@capacitor/geolocation';
import type { PermissionState } from '@capacitor/core';
import { ModalComponentService } from './ModalComponentService';
import AppLifecycleService from './AppLifecycleService';
import type { LifecycleHandler } from './AppLifecycleService';

/**
 * Estado de permiso que devuelven tanto Capacitor como el fallback de
 * navegador. Aqui solo se lee `location`, de ahi que no se exija el
 * PermissionStatus completo del plugin.
 */
export interface GeoPermissionResult {
  location: PermissionState;
}

/** Handler que registran los componentes para enterarse de los cambios. */
export type PermissionChangeHandler = (value: PermissionState) => void;

class PermissionsService {

  static geoPermissionGranted: boolean | undefined;
  static geoPermissionStatus: PermissionState | undefined;
  static appActiveHandler: LifecycleHandler = async () => {
    const status = await Geolocation.checkPermissions();
    const newStatus = status.location;
    if (newStatus === geoPermStatus.granted) {
      ModalComponentService.reset(null, ModalComponentService.ModalComponentTypes.PERMISSIONS_CARD);
    }
    if (newStatus !== this.geoPermissionStatus) {
      this.setState(newStatus);
    }
  };

  /**
   * Handler callback
   */
  static changePermissionHandler: PermissionChangeHandler | undefined;

  /**
   * Change permission status handler
   *
   * Parameters:
   * - value: permission status. One of geoPermStatus
   */
  static setState(value: PermissionState): void {
    log.debug('PermissionsService.setState', value);
    this.geoPermissionGranted = value === geoPermStatus.granted;
    this.geoPermissionStatus = value;

    // Handler callback
    if (this.changePermissionHandler) {
      this.changePermissionHandler(value);
    }
  }

  /**
   * Create change permissions handler and return i itial state
   *
   * Parameters:
   * - changeHandler: function fired on the component to change state
   */
  static async initGeoPermissionStatus(changeHandler: PermissionChangeHandler): Promise<boolean> {
    this.changePermissionHandler = changeHandler;

    // 1. iOS standalone (PWA instaladas)
    if (AppService.isIos() && AppService.isStandAlone()) {
      const result = await this.requestGeoPermission();
      this.setState(result.location);
      return result.location === geoPermStatus.granted;
    }

    // 2. Android Nativo con Capacitor (APP)
    if (AppService.isNativeApp()) {
      const result = await Geolocation.checkPermissions();
      this.setState(result.location);
      return result.location === geoPermStatus.granted;
    }

    // 3. Navegadores normales con navigator.permissions
    if ('permissions' in navigator) {
      const permission = await navigator.permissions.query({ name: 'geolocation' });
      permission.onchange = () => PermissionsService.setState(permission.state);
      this.setState(permission.state);
      return permission.state === geoPermStatus.granted;
    }

    // 4. Fallback navegador antiguo
    if ('geolocation' in navigator) {
      return new Promise<boolean>(resolve => {
        navigator.geolocation.getCurrentPosition(
          () => {
            this.setState(geoPermStatus.granted);
            resolve(true);
          },
          () => {
            this.setState(geoPermStatus.denied);
            resolve(false);
          }
        );
      });
    }

    return false;
  }

  /**
   * Force permissions prompt
   *
   * @returns Resultado con el estado del permiso ('granted', 'denied', 'prompt')
   */
  static async requestGeoPermission(): Promise<GeoPermissionResult> {
    if (AppService.isNativeApp()) {
      try {
        const result = await Geolocation.requestPermissions();
        return result;
      } catch (error) {
        log.warn('PermissionsService.requestGeoPermission error', error);
        // Si falla, verificar el estado actual
        try {
          const status = await Geolocation.checkPermissions();
          return status;
        } catch (checkError) {
          log.warn('PermissionsService.requestGeoPermission checkPermissions error', checkError);
          return { location: geoPermStatus.denied };
        }
      }
    } else {
      // Para navegadores, usar navigator.geolocation.getCurrentPosition
      // y determinar el estado basado en el resultado
      return new Promise<GeoPermissionResult>((resolve) => {
        if (!('geolocation' in navigator)) {
          resolve({ location: geoPermStatus.denied });
          return;
        }

        // Intentar obtener la posición para activar el prompt del navegador
        navigator.geolocation.getCurrentPosition(
          () => {
            // Éxito: el permiso está concedido
            resolve({ location: geoPermStatus.granted });
          },
          (error) => {
            // Error: determinar el estado según el código de error
            // Códigos de error de geolocation: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
            if (error.code === 1) {
              // PERMISSION_DENIED
              resolve({ location: geoPermStatus.denied });
            } else if (error.code === 2 || error.code === 3) {
              // POSITION_UNAVAILABLE o TIMEOUT - podría ser que aún esté en prompt
              // Intentar verificar con la API de permisos si está disponible
              if ('permissions' in navigator) {
                navigator.permissions.query({ name: 'geolocation' }).then((permission) => {
                  resolve({ location: permission.state });
                }).catch(() => {
                  resolve({ location: geoPermStatus.denied });
                });
              } else {
                resolve({ location: geoPermStatus.denied });
              }
            } else {
              resolve({ location: geoPermStatus.denied });
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      });
    }
  }

  /**
   * Ensure geolocation permission is granted
   *
   * @returns True if permission is granted, false otherwise
   */
  static async ensureGeoPermission(): Promise<boolean> {
    // Web: Mapbox ya desactiva el control si no hay API/permisos; aquí no bloqueamos nunca.
    if (!AppService.isNativeApp()) return true;

    // Native (Capacitor)
    try {
      // Verificar el estado actual de permisos
      const status = await Geolocation.checkPermissions();
      this.setState(status.location);

      // 1) Si ya está concedido
      if (status.location === geoPermStatus.granted) {
        return true;
      }

      // Android: checkPermissions puede devolver 'denied' incluso cuando el permiso está configurado como
      // "preguntar siempre" (no concedido todavía). En ese caso NO mandamos a Ajustes: pedimos permisos
      // para que salga el prompt del sistema.
      if (!AppService.isIos() && status.location === geoPermStatus.denied) {
        const result = await this.requestGeoPermission();
        this.setState(result.location);

        if (result.location === geoPermStatus.granted) {
          return true;
        }

        // Si tras pedir permisos sigue en denied, entonces sí es un "denied" efectivo (p.ej. denegado otra vez
        // o "no volver a preguntar") y mostramos la modal de ajustes.
        if (result.location === geoPermStatus.denied) {
          ModalComponentService.openModalComponent(
            ModalComponentService.ModalComponentTypes.PERMISSIONS_CARD
          );
          return false;
        }

        // Si queda en prompt, aplicamos la misma lógica que en el branch prompt (permitir que el click siga)
        // para que el control pueda pedir la ubicación y el sistema muestre el prompt cuando toque.
        return true;
      }

      // 2) Si el estado es 'prompt', intentar pedir permisos nativos (mostrará el prompt del sistema)
      if (status.location === geoPermStatus.prompt || (!AppService.isIos() && status.location === 'prompt-with-rationale')) {
        // iOS con "Preguntar siempre": requestPermissions/checkPermissions puede quedarse en 'prompt'.
        // En ese caso, NO bloqueamos: dejamos que el prompt real ocurra al pedir la posición (watchPosition/getCurrentPosition).
        if (AppService.isIos()) {
          return true;
        }

        const result = await this.requestGeoPermission();
        this.setState(result.location);

        if (result.location === geoPermStatus.granted) {
          return true;
        }

        // Android: si el usuario acaba de denegar en el prompt, no debemos "seguir adelante" ni re-disparar
        // el prompt automáticamente arrancando watchPosition. En estos casos cortamos y dejamos el botón en OFF.
        if (!AppService.isIos() && (result.location === geoPermStatus.denied || result.location === 'prompt-with-rationale')) {
          // Aqui habia un `if (status.location === denied)` que abria la modal de
          // Ajustes. Era codigo muerto: para entrar en esta rama status.location
          // ya es 'prompt' o 'prompt-with-rationale', nunca 'denied'. El efecto
          // era el que se buscaba —no abrir Ajustes en el primer deny—, asi que
          // se elimina la comprobacion y el comportamiento no cambia.
          return false;
        }

        // Android: tras elegir “solo esta vez”/“preguntar siempre”, requestPermissions puede devolver 'prompt'
        // pero el permiso puede pasar a 'granted' justo al cerrarse el diálogo. Esperamos un poco.
        if (!AppService.isIos() && result.location === geoPermStatus.prompt) {
          const start = Date.now();
          while (Date.now() - start < 2500) {
            await new Promise<void>((r) => setTimeout(r, 200));
            const after = await Geolocation.checkPermissions();
            if (after?.location && after.location !== geoPermStatus.prompt && after.location !== 'prompt-with-rationale') {
              this.setState(after.location);
              return after.location === geoPermStatus.granted;
            }
          }

          // Android con "Preguntar siempre": el estado puede quedarse en 'prompt' indefinidamente aunque el usuario
          // pueda conceder "solo esta vez" al pedir la ubicación. No bloqueamos en este caso.
          return true;
        }

        return false;
      }

      // 3) Si el estado es 'denied' (denegado permanentemente), mostramos la modal de ajustes
      if (status.location === geoPermStatus.denied) {
        ModalComponentService.openModalComponent(
          ModalComponentService.ModalComponentTypes.PERMISSIONS_CARD
        );
      }

      return false;
    } catch (error) {
      log.warn('PermissionsService.ensureGeoPermission error requesting native permissions', error);
      ModalComponentService.openModalComponent(
        ModalComponentService.ModalComponentTypes.PERMISSIONS_CARD
      );
      return false;
    }
  }

  /**
   * Registers the app active handler used to refresh permissions when the app
   * returns to the foreground.
   */
  static initAppActiveHandler(): void {
    AppLifecycleService.addAppActiveHandler(this.appActiveHandler);
  }

}

export default PermissionsService;
